<template>
  <v-expansion-panels v-if="gruposAjustes.length" accordion flat>
    <v-expansion-panel v-for="grupo in gruposAjustes" :key="grupo.tipo">
      <v-expansion-panel-header class="body-2 font-weight-medium px-0">
        {{ grupo.rotulo }} ({{ grupo.ajustes.length }})
      </v-expansion-panel-header>
      <v-expansion-panel-content class="px-0">
        <v-list dense>
          <v-list-item v-for="ajuste in grupo.ajustes" :key="ajuste.chave" class="px-0">
            <v-list-item-icon class="mr-3">
              <v-icon color="primary">{{ iconeDoTipo(grupo.tipo) }}</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>{{ tituloDoAjuste(ajuste) }}</v-list-item-title>
              <v-list-item-subtitle>{{ detalhesDoAjuste(ajuste) }}</v-list-item-subtitle>
            </v-list-item-content>
            <v-list-item-action v-if="permitirDownload || permitirRemocao" class="align-center d-flex flex-row">
              <v-btn
                v-if="permitirDownload"
                :aria-label="`Baixar ${tituloDoAjuste(ajuste)}`"
                :loading="baixando === ajuste.chave"
                color="primary"
                icon
                small
                type="button"
                @click="baixarAjuste(ajuste)"
              >
                <v-icon small>mdi-download</v-icon>
              </v-btn>
              <v-btn
                v-if="permitirRemocao"
                :aria-label="`Remover ${tituloDoAjuste(ajuste)}`"
                color="primary"
                icon
                small
                type="button"
                @click="$emit('remover', ajuste)"
              >
                <v-icon small>mdi-close</v-icon>
              </v-btn>
            </v-list-item-action>
          </v-list-item>
        </v-list>
      </v-expansion-panel-content>
    </v-expansion-panel>
  </v-expansion-panels>
</template>

<script>
  import AtualizacoesBdgdService from '@/services/AtualizacoesBdgdService.js';
  import AtualizacoesBdgdViaScriptModeloService from '@/services/AtualizacoesBdgdViaScriptModeloService.js';
  import AtualizacoesBdgdViaSqlService from '@/services/AtualizacoesBdgdViaSqlService.js';
  import GestaoBdgdsErrosTarefasService from '@/services/GestaoBdgdsErrosTarefasService.js';

  const iconesPorTipo = {
    EM_MASSA: 'mdi-database-edit-outline',
    EM_TELA: 'mdi-monitor-edit',
    SCRIPT: 'mdi-code-braces',
    VIA_EXCEL: 'mdi-file-excel-outline'
  };

  const tiposAjuste = [
    { text: 'Via Excel', value: 'VIA_EXCEL' },
    { text: 'Em massa', value: 'EM_MASSA' },
    { text: 'Script', value: 'SCRIPT' },
    { text: 'Em tela', value: 'EM_TELA' }
  ];

  export default {
    name: 'AjustesSelecionadosAgrupados',

    props: {
      ajustes: {
        type: Array,
        default: () => []
      },
      permitirRemocao: {
        type: Boolean,
        default: false
      },
      permitirDownload: {
        type: Boolean,
        default: false
      },
      erroId: {
        type: [Number, String],
        default: null
      }
    },

    data() {
      return {
        baixando: ''
      };
    },

    computed: {
      gruposAjustes() {
        return tiposAjuste
          .map((tipo) => ({
            tipo: tipo.value,
            rotulo: tipo.text,
            ajustes: this.ajustes.filter((ajuste) => ajuste.tipo_ajuste === tipo.value)
          }))
          .filter(({ ajustes }) => ajustes.length);
      }
    },

    methods: {
      iconeDoTipo(tipo) {
        return iconesPorTipo[tipo] || 'mdi-file-document-outline';
      },

      tituloDoAjuste(ajuste) {
        return ajuste.descricao || ajuste.titulo || ajuste.arquivo || ajuste.entidade_exibicao || `Ajuste #${ajuste.id}`;
      },

      detalhesDoAjuste(ajuste) {
        const detalhes = [
          ajuste.id ? `#${ajuste.id}` : null,
          ajuste.entidade_exibicao,
          ajuste.campo,
          ajuste.cod_id ? `COD_ID: ${ajuste.cod_id}` : null,
          ajuste.empresa,
          ajuste.script_modelo_nome,
          ajuste.numero_linhas ? `${ajuste.numero_linhas} linhas` : null,
          ajuste.status
        ].filter(Boolean);

        return detalhes.join(' • ') || ajuste.detalhe || '—';
      },

      async baixarArquivo(chave, requisicao, nomeArquivo) {
        this.baixando = chave;

        try {
          const { data } = await requisicao();
          const url = window.URL.createObjectURL(new Blob([data]));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', nomeArquivo);
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
        } catch (error) {
          this.$toast.error('Não foi possível baixar o ajuste.', '', { position: 'topRight' });
        } finally {
          this.baixando = '';
        }
      },

      baixarAjuste(ajuste) {
        if (ajuste.tipo_ajuste === 'EM_TELA') return this.baixarAjusteEmTela(ajuste);

        if (this.erroId) {
          return this.baixarArquivo(
            ajuste.chave,
            () =>
              GestaoBdgdsErrosTarefasService.baixarArquivoEvidencia(this.erroId, {
                tipo: 'correcao',
                id: ajuste.id,
                tabela: ajuste.tabela_ajuste
              }),
            `${ajuste.tabela_ajuste}-${ajuste.id}.zip`
          );
        }

        const download = {
          EM_MASSA: {
            nomeArquivo: `atualizacao-bdgd-via-sql-${ajuste.id}.zip`,
            requisicao: () => AtualizacoesBdgdViaSqlService.baixarZip(ajuste.id)
          },
          SCRIPT: {
            nomeArquivo: `atualizacao-bdgd-script-modelo-${ajuste.id}.zip`,
            requisicao: () => AtualizacoesBdgdViaScriptModeloService.baixarZip(ajuste.id)
          },
          VIA_EXCEL: {
            nomeArquivo: `atualizacao-bdgd-${ajuste.id}.zip`,
            requisicao: () => AtualizacoesBdgdService.baixarZip(ajuste.id)
          }
        }[ajuste.tipo_ajuste];

        if (!download) return;

        return this.baixarArquivo(ajuste.chave, download.requisicao, download.nomeArquivo);
      },

      baixarAjusteEmTela(ajuste) {
        const url = window.URL.createObjectURL(new Blob([JSON.stringify(ajuste, null, 2)], { type: 'application/json' }));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `ajuste-em-tela-${ajuste.id}.json`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      }
    }
  };
</script>
